import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/**
 * Live Tests admin — schedule a fixed-time competition.
 */
export default function LiveTestsAdmin() {
  const [tests, setTests] = useState([]);
  const [modItems, setModItems] = useState({ dictation: [], legal: [], pitman: [], typing: [] });
  const [form, setForm] = useState({
    title: "", description: "", module: "typing", item_id: "",
    scheduled_at_local: "", duration_minutes: 15,
  });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/live-tests").then((r) => setTests(r.data));
  useEffect(() => {
    load();
    Promise.all([
      api.get("/dictations").then((r) => ["dictation", r.data]),
      api.get("/legal-passages").then((r) => ["legal", r.data]),
      api.get("/pitman-exercises").then((r) => ["pitman", r.data]),
      api.get("/typing-passages").then((r) => ["typing", r.data]),
    ]).then((pairs) => {
      const map = {};
      pairs.forEach(([k, v]) => { map[k] = v; });
      setModItems(map);
    }).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.item_id) { toast.error("Choose an exercise"); return; }
    if (!form.scheduled_at_local) { toast.error("Pick a scheduled date/time"); return; }
    setBusy(true);
    try {
      // datetime-local gives "2026-08-10T18:00" (no timezone) — treat as local time
      const scheduled = new Date(form.scheduled_at_local).toISOString();
      await api.post("/admin/live-tests", {
        title: form.title, description: form.description || undefined,
        module: form.module, item_id: form.item_id,
        scheduled_at: scheduled,
        duration_seconds: Math.max(60, Math.min(7200, form.duration_minutes * 60)),
        is_published: true,
      });
      toast.success("Live test scheduled");
      setForm({ title: "", description: "", module: "typing", item_id: "", scheduled_at_local: "", duration_minutes: 15 });
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this test? All registrations will be lost.")) return;
    await api.delete(`/admin/live-tests/${id}`);
    load();
  };

  const togglePublish = async (t) => {
    await api.patch(`/admin/live-tests/${t.id}`, { is_published: !t.is_published });
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={create} className="border border-black/20 p-6 space-y-4" data-testid="admin-livetest-form">
        <div className="text-xs uppercase tracking-[0.3em] font-bold">Schedule live test</div>
        <Input required placeholder="Title (e.g. Weekly Speed Test 5 Aug)"
          value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="rounded-none font-mono" data-testid="admin-livetest-title" />
        <Textarea placeholder="Description (rules, prize, syllabus)"
          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="rounded-none font-mono" data-testid="admin-livetest-description" />

        <div className="grid grid-cols-2 gap-3">
          <Select value={form.module} onValueChange={(v) => setForm({ ...form, module: v, item_id: "" })}>
            <SelectTrigger className="rounded-none font-mono" data-testid="admin-livetest-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dictation">Dictation</SelectItem>
              <SelectItem value="legal">Legal</SelectItem>
              <SelectItem value="pitman">Pitman</SelectItem>
              <SelectItem value="typing">Typing</SelectItem>
            </SelectContent>
          </Select>
          <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
            <SelectTrigger className="rounded-none font-mono" data-testid="admin-livetest-item"><SelectValue placeholder="Choose exercise" /></SelectTrigger>
            <SelectContent>
              {(modItems[form.module] || []).map((x) => (
                <SelectItem key={x.id} value={x.id}>{x.title || x.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Start (local)</Label>
            <Input type="datetime-local" required
              value={form.scheduled_at_local}
              onChange={(e) => setForm({ ...form, scheduled_at_local: e.target.value })}
              className="rounded-none font-mono mt-1" data-testid="admin-livetest-scheduled" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Duration (min)</Label>
            <Input type="number" min="1" max="120"
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
              className="rounded-none font-mono mt-1" data-testid="admin-livetest-duration" />
          </div>
        </div>

        <Button type="submit" disabled={busy} className="rounded-none w-full h-11 bg-[#DC2626] hover:bg-red-700 text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="admin-livetest-submit-btn">
          {busy ? "Scheduling…" : "Schedule test"}
        </Button>
      </form>

      <div>
        <h3 className="text-lg font-black tracking-tight mb-3">Scheduled tests</h3>
        <div className="border border-black/15 divide-y divide-black/10" data-testid="admin-livetest-list">
          {tests.length === 0 && <div className="p-4 font-mono text-sm text-neutral-500">None yet.</div>}
          {tests.map((t) => (
            <div key={t.id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold truncate">{t.title}</div>
                <div className="text-xs font-mono text-neutral-500">
                  {t.module} · {new Date(t.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {t.registered_count} registered · {t.state} · {t.is_published ? "Published" : "Draft"}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button onClick={() => togglePublish(t)} variant="outline" size="sm" className="rounded-none border-black/15" data-testid={`admin-livetest-toggle-${t.id}`}>
                  {t.is_published ? "Unpublish" : "Publish"}
                </Button>
                <Button onClick={() => del(t.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-livetest-delete-${t.id}`}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
